import type { ExtendResource, Extension, Turn } from "@yb/core";
import type { Board } from "./api";
import { hhmm } from "./format";

/**
 * Allocations held past their usual end by a planner: the stand, the check-in desks, the
 * reclaim belt or the outbound belt. Shared by the context menu, the dialog, the bars on
 * all four tabs and the hover card, so each says the same thing about the same extension.
 */

const MIN = 60_000;

/** What is held longer, in the words of the board's tabs. */
export const EXTEND_WHAT: Record<ExtendResource, string> = { stand: "Stand", counters: "Check-in", belts: "Reclaim belt", outbound: "Outbound belt" };

/**
 * The flight an extension on this resource is for. A counter bar can stand for several of
 * one airline's flights back to back; it is the last of them whose desks stay open longer.
 */
export function extendTarget(board: Board, resource: ExtendResource, turnId: string, legIds: string[] = []): Turn | undefined {
  if (resource === "stand" || legIds.length === 0) return board.turns.find((t) => t.id === turnId);
  const list = resource === "counters" ? board.plan.counters : resource === "belts" ? board.plan.belts : board.plan.outbound ?? {};
  const last = [...legIds].sort((a, b) => (list[b]?.to ?? 0) - (list[a]?.to ?? 0))[0];
  return board.turns.find((t) => (resource === "belts" ? t.arr?.id === last : t.dep?.id === last));
}

/**
 * Where an allocation ends without any extension, and where it ends now. A towed visit
 * ends on its last segment, which is the part a stand extension lengthens.
 */
export function extendSpan(board: Board, resource: ExtendResource, t: Turn): { usual: number; end: number; ext: Extension | null } | null {
  if (resource === "stand") {
    const root = t.id.replace(/#T\d+$/, "");
    const last = board.turns.filter((x) => x.id === root || x.id.startsWith(`${root}#T`)).reduce((a, b) => (b.end > a.end ? b : a), t);
    const ext = last.extensions?.stand ?? null;
    return { usual: ext?.from ?? last.end, end: last.end, ext };
  }
  const leg = resource === "belts" ? t.arr : t.dep;
  const a = leg ? (resource === "counters" ? board.plan.counters : resource === "belts" ? board.plan.belts : board.plan.outbound ?? {})[leg.id] : undefined;
  if (!a) return null;
  return { usual: a.extendedFrom ?? a.to, end: a.to, ext: a.extendedFrom !== undefined ? (t.extensions?.[resource] ?? null) : null };
}

/** "Check-in extended by 30 min to 10:30 by Pat: crew late", for the hover card and the bar's tooltip. */
export function extensionText(resource: ExtendResource, ext: Pick<Extension, "until" | "note" | "by">, usual: number, tz: string): string {
  const mins = Math.round((ext.until - usual) / MIN);
  return `${EXTEND_WHAT[resource]} extended by ${mins} min to ${hhmm(ext.until, tz)}${ext.by ? ` by ${ext.by}` : ""}${ext.note ? `: ${ext.note}` : ""}`;
}

